import type { DelayMode, DelayUnit } from "./types";

// =============================================================================
// TimeSpan Parsing & Formatting
// =============================================================================

/**
 * Regex for .NET TimeSpan format: optional days prefix, then hh:mm:ss
 * Matches: "00:05:00", "02:30:00", "1.00:00:00", "7.12:00:00"
 */
const TIMESPAN_REGEX = /^(?:(\d+)\.)?(\d{2}):(\d{2}):(\d{2})$/;

/**
 * Parse a .NET TimeSpan string into total seconds.
 * Returns null if the string is not a valid TimeSpan.
 */
export function parseTimeSpan(
  value: string
): { days: number; hours: number; minutes: number; seconds: number; totalSeconds: number } | null {
  const match = TIMESPAN_REGEX.exec(value);
  if (!match) return null;

  const days = match[1] ? Number.parseInt(match[1], 10) : 0;
  const hours = Number.parseInt(match[2], 10);
  const minutes = Number.parseInt(match[3], 10);
  const seconds = Number.parseInt(match[4], 10);

  if (hours > 23 || minutes > 59 || seconds > 59) return null;

  const totalSeconds = days * 86400 + hours * 3600 + minutes * 60 + seconds;
  return { days, hours, minutes, seconds, totalSeconds };
}

/**
 * Convert a numeric value and unit to a .NET TimeSpan string.
 * e.g. (5, "minutes") -> "00:05:00"
 * e.g. (2, "days") -> "2.00:00:00"
 */
export function durationToTimeSpan(value: number, unit: DelayUnit): string {
  const totalSeconds = durationToSeconds(value, unit);
  return secondsToTimeSpan(totalSeconds);
}

/**
 * Convert a duration value + unit to total seconds.
 */
function durationToSeconds(value: number, unit: DelayUnit): number {
  switch (unit) {
    case "seconds":
      return value;
    case "minutes":
      return value * 60;
    case "hours":
      return value * 3600;
    case "days":
      return value * 86400;
  }
}

/**
 * Convert total seconds to .NET TimeSpan string.
 */
function secondsToTimeSpan(totalSeconds: number): string {
  const days = Math.floor(totalSeconds / 86400);
  const remaining = totalSeconds % 86400;
  const hours = Math.floor(remaining / 3600);
  const minutes = Math.floor((remaining % 3600) / 60);
  const seconds = remaining % 60;

  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");

  if (days > 0) {
    return `${days}.${hh}:${mm}:${ss}`;
  }
  return `${hh}:${mm}:${ss}`;
}

/**
 * Convert a TimeSpan string to the best-fit duration value and unit.
 * e.g. "00:05:00" -> { value: 5, unit: "minutes" }
 * e.g. "1.00:00:00" -> { value: 1, unit: "days" }
 */
export function timeSpanToDuration(ts: string): { value: number; unit: DelayUnit } | null {
  const parsed = parseTimeSpan(ts);
  if (!parsed) return null;

  const { totalSeconds } = parsed;
  if (totalSeconds === 0) return { value: 0, unit: "seconds" };

  // Try to find the best-fitting unit (largest unit that divides evenly)
  if (totalSeconds % 86400 === 0) {
    return { value: totalSeconds / 86400, unit: "days" };
  }
  if (totalSeconds % 3600 === 0) {
    return { value: totalSeconds / 3600, unit: "hours" };
  }
  if (totalSeconds % 60 === 0) {
    return { value: totalSeconds / 60, unit: "minutes" };
  }
  return { value: totalSeconds, unit: "seconds" };
}

// =============================================================================
// DateTimeOffset Parsing & Formatting
// =============================================================================

/**
 * Check if a string looks like an ISO 8601 DateTimeOffset.
 * Must contain a date separator (T or space) and parse as a valid Date.
 */
export function isDateTimeOffset(value: string): boolean {
  // Must look like a date (contain a T or have the YYYY-MM-DD pattern)
  if (!/^\d{4}-\d{2}-\d{2}[T ]/.test(value)) return false;
  const d = new Date(value);
  return !Number.isNaN(d.getTime());
}

/**
 * Check if a string is a valid .NET TimeSpan format.
 */
export function isTimeSpan(value: string): boolean {
  return TIMESPAN_REGEX.test(value);
}

/**
 * Get the local timezone offset for a specific date as an ISO 8601 string
 * (e.g., "+05:00", "-04:00", "Z").
 * Uses the offset at the given date to account for DST changes.
 */
function getTimezoneOffset(date: Date): string {
  const offsetMinutes = date.getTimezoneOffset();
  if (offsetMinutes === 0) return "Z";

  const sign = offsetMinutes < 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hh = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const mm = String(absMinutes % 60).padStart(2, "0");
  return `${sign}${hh}:${mm}`;
}

/**
 * Convert a Date object and 24-hour time string to an ISO 8601 DateTimeOffset string
 * using the timezone offset at the selected date/time (DST-aware).
 * e.g. (new Date("2026-06-15"), "14:30") -> "2026-06-15T14:30:00-04:00"
 */
export function dateTimeToISO(date: Date, time: string): string {
  const [hours, minutes] = time.split(":").map(Number);
  const h = hours || 0;
  const m = minutes || 0;

  // Construct a Date at the selected date + time to get the correct DST offset
  const target = new Date(date.getFullYear(), date.getMonth(), date.getDate(), h, m, 0);

  const year = target.getFullYear();
  const month = String(target.getMonth() + 1).padStart(2, "0");
  const day = String(target.getDate()).padStart(2, "0");
  const hh = String(target.getHours()).padStart(2, "0");
  const mm = String(target.getMinutes()).padStart(2, "0");

  const offset = getTimezoneOffset(target);
  return `${year}-${month}-${day}T${hh}:${mm}:00${offset}`;
}

/**
 * Parse an ISO 8601 string into a local Date and 24-hour time string.
 * Uses local time so the date/time picker reflects what the user originally selected.
 * e.g. "2026-06-15T14:30:00-04:00" -> { date: Date(Jun 15), time: "14:30" }
 */
export function isoToDateTime(iso: string): { date: Date; time: string } | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  // Create a date-only Date using local time (for the calendar picker)
  const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());

  return { date: dateOnly, time: `${hours}:${minutes}` };
}

// =============================================================================
// Mode Detection
// =============================================================================

/**
 * Detect the delay mode from a stored string value.
 * Returns the mode and parsed values for the settings UI.
 */
export function detectDelayMode(value: string): DelayMode {
  if (!value) return "duration";

  // Try TimeSpan first (same priority as backend)
  if (isTimeSpan(value)) return "duration";

  // Try DateTimeOffset
  if (isDateTimeOffset(value)) return "datetime";

  // Default to duration
  return "duration";
}

// =============================================================================
// Display Formatting
// =============================================================================

/**
 * Format a TimeSpan value for human-readable display on the node.
 * e.g. "00:05:00" -> "5 min"
 * e.g. "1.00:00:00" -> "1 day"
 */
export function formatTimeSpanDisplay(ts: string): string {
  const duration = timeSpanToDuration(ts);
  if (!duration) return ts;

  const { value, unit } = duration;
  const unitLabels: Record<DelayUnit, [string, string]> = {
    seconds: ["sec", "sec"],
    minutes: ["min", "min"],
    hours: ["hr", "hrs"],
    days: ["day", "days"],
  };

  const [singular, plural] = unitLabels[unit];
  return `${value} ${value === 1 ? singular : plural}`;
}

/**
 * Format a DateTimeOffset for human-readable display on the node.
 * Displays in the user's local timezone.
 * e.g. "2026-06-15T14:30:00-04:00" -> "Jun 15, 2026 2:30 PM"
 */
export function formatDateTimeDisplay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;

  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Format a delay value for display on the node canvas.
 * Detects whether it's a TimeSpan or DateTimeOffset and formats accordingly.
 */
export function formatDelayDisplay(value: string): string {
  if (!value) return "";

  if (isTimeSpan(value)) {
    return formatTimeSpanDisplay(value);
  }

  if (isDateTimeOffset(value)) {
    return formatDateTimeDisplay(value);
  }

  return value;
}
